/**
 * Loads OSM waterway GeoJSON based on the current map viewport.
 * Triggers a reload when the user pans/zooms beyond the previously-loaded area.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useMap } from '../ui/map';
import { api } from '../../lib/api';

interface ViewportWaterwayLoaderProps {
  onDataLoaded: (data: GeoJSON.FeatureCollection) => void;
  onLoadingChange?: (loading: boolean) => void;
}

/** Expand a bbox by a factor (e.g. 0.5 = 50% padding on each side). */
function expandBbox(
  bbox: { south: number; west: number; north: number; east: number },
  factor: number,
) {
  const latPad = (bbox.north - bbox.south) * factor;
  const lngPad = (bbox.east - bbox.west) * factor;
  return {
    south: Math.max(-90, bbox.south - latPad),
    west: Math.max(-180, bbox.west - lngPad),
    north: Math.min(90, bbox.north + latPad),
    east: Math.min(180, bbox.east + lngPad),
  };
}

/** Check if the viewport is fully contained within the loaded bbox. */
function isContained(
  viewport: { south: number; west: number; north: number; east: number },
  loaded: { south: number; west: number; north: number; east: number },
) {
  return (
    viewport.south >= loaded.south &&
    viewport.west >= loaded.west &&
    viewport.north <= loaded.north &&
    viewport.east <= loaded.east
  );
}

export function ViewportWaterwayLoader({
  onDataLoaded,
  onLoadingChange,
}: ViewportWaterwayLoaderProps) {
  const { map, isLoaded } = useMap();
  const loadedBboxRef = useRef<{
    south: number;
    west: number;
    north: number;
    east: number;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDone = useRef(false);

  const loadForBounds = useCallback(
    async (bounds: { south: number; west: number; north: number; east: number }) => {
      // Fetch with an expanded bbox so small pans don't trigger reloads
      const fetchBbox = expandBbox(bounds, 0.5);

      // If the current viewport is fully inside what we already loaded, skip
      if (loadedBboxRef.current && isContained(bounds, loadedBboxRef.current)) {
        return;
      }

      // Cancel any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      onLoadingChange?.(true);
      try {
        const data = await api.waterways.getOsmWaterwaysGeoJSON({ bbox: fetchBbox });

        // Check that this request wasn't aborted while waiting
        if (controller.signal.aborted) return;

        loadedBboxRef.current = fetchBbox;
        onDataLoaded(data);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('[ViewportWaterwayLoader] Failed to load waterways:', err);
      } finally {
        if (!controller.signal.aborted) {
          onLoadingChange?.(false);
        }
      }
    },
    [onDataLoaded, onLoadingChange],
  );

  const loadFromMap = useCallback(() => {
    if (!map) return;
    const bounds = map.getBounds();
    loadForBounds({
      south: bounds.getSouth(),
      west: bounds.getWest(),
      north: bounds.getNorth(),
      east: bounds.getEast(),
    });
  }, [map, loadForBounds]);

  const debouncedLoadFromMap = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadFromMap, 300);
  }, [loadFromMap]);

  // Initial load when map is ready
  useEffect(() => {
    if (!map || !isLoaded || initialLoadDone.current) return;
    initialLoadDone.current = true;
    loadFromMap();
  }, [map, isLoaded, loadFromMap]);

  // Reload when viewport changes
  useEffect(() => {
    if (!map || !isLoaded) return;

    map.on('moveend', debouncedLoadFromMap);
    return () => {
      map.off('moveend', debouncedLoadFromMap);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [map, isLoaded, debouncedLoadFromMap]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return null;
}
